import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './CustomersTab.css';
import { format } from 'date-fns';
import { customersAPI, paymentsAPI, employeesAPI, employeeCreditsAPI } from '../services/api';
import ConfirmModal from './ConfirmModal';
import { useToast } from '../hooks/useToast';
import { debounce } from '../utils/debounce';

const CustomersTab = ({ settings }) => {
  const [activeSection, setActiveSection] = useState('customers'); // 'customers' or 'employees'
  const { success, error: showError } = useToast();
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, onConfirm: null, title: '', message: '', type: 'warning' });
  
  // Customers state
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerCredits, setCustomerCredits] = useState([]);
  const [customerPayments, setCustomerPayments] = useState([]);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    payment_method: 'cash',
    notes: ''
  });

  // Employees state
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeCredits, setEmployeeCredits] = useState([]);

  useEffect(() => {
    if (activeSection === 'customers') {
      loadCustomers();
    } else {
      loadEmployees();
    }
  }, [activeSection]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const data = await customersAPI.getCreditsSummary();
      setCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
      showError('Failed to load customers: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      setEmployeesLoading(true);
      const data = await employeesAPI.getAll();
      setEmployees(data);
    } catch (error) {
      console.error('Error loading employees:', error);
      showError('Failed to load employees: ' + error.message);
    } finally {
      setEmployeesLoading(false);
    }
  };

  const handleCustomerClick = async (customer) => {
    try {
      const creditsData = await customersAPI.getCredits(customer._id || customer.id);
      setSelectedCustomer(creditsData);
      setCustomerCredits(creditsData.credits || []);
      setCustomerPayments(creditsData.payments || []);
      setShowPaymentForm(false);
    } catch (error) {
      console.error('Error loading customer credits:', error);
      showError('Failed to load customer credits: ' + error.message);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      showError('Please enter a valid payment amount');
      return;
    }

    if (!selectedCustomer) {
      showError('Please select a customer first');
      return;
    }

    try {
      const payment = {
        customer_name: selectedCustomer.customer.name,
        amount: parseFloat(paymentForm.amount),
        date: paymentForm.date,
        payment_method: paymentForm.payment_method,
        notes: paymentForm.notes
      };

      await paymentsAPI.create(payment);
      
      // Refresh customer data
      await handleCustomerClick(selectedCustomer.customer);
      
      // Refresh customers list
      await loadCustomers();
      
      // Reset form
      setPaymentForm({
        amount: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        payment_method: 'cash',
        notes: ''
      });
      setShowPaymentForm(false);
      
      success('Payment recorded successfully!');
    } catch (error) {
      console.error('Error saving payment:', error);
      showError('Failed to save payment: ' + error.message);
    }
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setPaymentForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const filteredCustomers = useMemo(() => {
    if (!searchQuery) return customers;
    return customers.filter(customer =>
      customer.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [customers, searchQuery]);

  const sortedCustomers = useMemo(() => {
    return [...filteredCustomers].sort((a, b) => (b.balance || 0) - (a.balance || 0));
  }, [filteredCustomers]);

  const handleDeletePayment = async (paymentId) => {
    showConfirm(
      'Delete Payment',
      'Are you sure you want to delete this payment? This action cannot be undone.',
      async () => {
        try {
          await paymentsAPI.delete(paymentId);
          await handleCustomerClick(selectedCustomer.customer);
          await loadCustomers();
          success('Payment deleted successfully');
        } catch (error) {
          console.error('Error deleting payment:', error);
          showError('Failed to delete payment: ' + error.message);
        }
      },
      'danger'
    );
  };

  const handleDeleteCredit = async (creditId) => {
    showConfirm(
      'Delete Credit',
      'Are you sure you want to delete this credit? This action cannot be undone.',
      async () => {
        try {
          const { creditsAPI } = await import('../services/api');
          await creditsAPI.delete(creditId);
          await handleCustomerClick(selectedCustomer.customer);
          await loadCustomers();
          success('Credit deleted successfully');
        } catch (error) {
          console.error('Error deleting credit:', error);
          showError('Failed to delete credit: ' + error.message);
        }
      },
      'danger'
    );
  };

  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [newEmployeeForm, setNewEmployeeForm] = useState({ name: '', monthly_salary: '' });
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [newCustomerForm, setNewCustomerForm] = useState({ name: '', phone: '', email: '', notes: '' });

  const handleAddNewEmployee = async (e) => {
    if (e) e.preventDefault();
    if (!newEmployeeForm.name || !newEmployeeForm.name.trim()) {
      showError('Please enter an employee name');
      return;
    }

    if (!newEmployeeForm.monthly_salary || parseFloat(newEmployeeForm.monthly_salary) < 0) {
      showError('Please enter a valid monthly salary');
      return;
    }

    try {
      await employeesAPI.create({
        name: newEmployeeForm.name.trim(),
        monthly_salary: parseFloat(newEmployeeForm.monthly_salary) || 0
      });
      await loadEmployees();
      setNewEmployeeForm({ name: '', monthly_salary: '' });
      setShowEmployeeForm(false);
      success('Employee added successfully!');
    } catch (error) {
      console.error('Error adding employee:', error);
      showError('Failed to add employee: ' + error.message);
    }
  };

  const handleAddNewCustomer = async (e) => {
    if (e) e.preventDefault();
    if (!newCustomerForm.name || !newCustomerForm.name.trim()) {
      showError('Please enter a customer name');
      return;
    }

    try {
      await customersAPI.create({
        name: newCustomerForm.name.trim(),
        phone: newCustomerForm.phone.trim() || undefined,
        email: newCustomerForm.email.trim() || undefined,
        notes: newCustomerForm.notes.trim() || undefined
      });
      await loadCustomers();
      setNewCustomerForm({ name: '', phone: '', email: '', notes: '' });
      setShowCustomerForm(false);
      success('Customer added successfully!');
    } catch (error) {
      console.error('Error adding customer:', error);
      showError('Failed to add customer: ' + error.message);
    }
  };

  const handleEmployeeFormChange = (e) => {
    const { name, value } = e.target;
    setNewEmployeeForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCustomerFormChange = (e) => {
    const { name, value } = e.target;
    setNewCustomerForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const filteredEmployees = useMemo(() => {
    if (!employeeSearchQuery) return employees;
    return employees.filter(employee =>
      employee.name.toLowerCase().includes(employeeSearchQuery.toLowerCase())
    );
  }, [employees, employeeSearchQuery]);

  const handleEmployeeClick = async (employee) => {
    try {
      const employeeData = await employeesAPI.getById(employee._id || employee.id);
      setSelectedEmployee({ employee: employeeData });
      
      // Load employee credits
      const credits = await employeeCreditsAPI.getByEmployee(employee.name);
      setEmployeeCredits(credits || []);
    } catch (error) {
      console.error('Error loading employee data:', error);
      showError('Failed to load employee data: ' + error.message);
    }
  };

  const handleDeleteEmployeeCredit = async (creditId) => {
    showConfirm(
      'Delete Employee Credit',
      'Are you sure you want to delete this employee credit? This action cannot be undone.',
      async () => {
        try {
          await employeeCreditsAPI.delete(creditId);
          await handleEmployeeClick(selectedEmployee.employee);
          success('Employee credit deleted successfully');
        } catch (error) {
          console.error('Error deleting employee credit:', error);
          showError('Failed to delete employee credit: ' + error.message);
        }
      },
      'danger'
    );
  };

  const handleDeleteCustomer = async (customer) => {
    showConfirm(
      'Delete Customer',
      `Are you sure you want to delete "${customer.name}"? This will also delete all associated credits and payments. This action cannot be undone.`,
      async () => {
        try {
          await customersAPI.delete(customer._id || customer.id);
          setSelectedCustomer(null);
          setCustomerCredits([]);
          setCustomerPayments([]);
          await loadCustomers();
          success('Customer and all associated credits/payments deleted successfully');
        } catch (error) {
          console.error('Error deleting customer:', error);
          showError('Failed to delete customer: ' + error.message);
        }
      },
      'danger'
    );
  };

  const handleDeleteEmployee = async (employee) => {
    showConfirm(
      'Delete Employee',
      `Are you sure you want to delete "${employee.name}"? This will also delete all associated credits. This action cannot be undone.`,
      async () => {
        try {
          await employeesAPI.delete(employee._id || employee.id);
          setSelectedEmployee(null);
          setEmployeeCredits([]);
          await loadEmployees();
          success('Employee and all associated credits deleted successfully');
        } catch (error) {
          console.error('Error deleting employee:', error);
          showError('Failed to delete employee: ' + error.message);
        }
      },
      'danger'
    );
  };

  const showConfirm = (title, message, onConfirm, type = 'warning') => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      type,
      onConfirm: () => {
        setConfirmModal({ isOpen: false, onConfirm: null, title: '', message: '', type: 'warning' });
        onConfirm();
      }
    });
  };

  return (
    <div className="customers-tab">
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, onConfirm: null, title: '', message: '', type: 'warning' })}
        onConfirm={confirmModal.onConfirm || (() => {})}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
      />
      <div className="tab-header">
        <h2>Customers & Employees</h2>
        <div className="section-tabs">
          <button
            className={activeSection === 'customers' ? 'active' : ''}
            onClick={() => setActiveSection('customers')}
          >
            Customers
          </button>
          <button
            className={activeSection === 'employees' ? 'active' : ''}
            onClick={() => setActiveSection('employees')}
          >
            Employees
          </button>
        </div>
      </div>

      {activeSection === 'customers' && (
      <div className="customers-container">
        <div className="customers-list-panel">
          <div className="list-header">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search customers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button 
              onClick={() => setShowCustomerForm(!showCustomerForm)} 
              className="btn btn-primary btn-add"
            >
              {showCustomerForm ? 'Cancel' : '+ Add Customer'}
            </button>
          </div>

          {showCustomerForm && (
            <form onSubmit={handleAddNewCustomer} className="add-form">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  name="name"
                  value={newCustomerForm.name}
                  onChange={handleCustomerFormChange}
                  placeholder="Customer name"
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="text"
                  name="phone"
                  value={newCustomerForm.phone}
                  onChange={handleCustomerFormChange}
                  placeholder="Phone number"
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={newCustomerForm.email}
                  onChange={handleCustomerFormChange}
                  placeholder="Email address"
                />
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={newCustomerForm.notes}
                  onChange={handleCustomerFormChange}
                  placeholder="Additional notes"
                  rows="2"
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  Add Customer
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowCustomerForm(false);
                    setNewCustomerForm({ name: '', phone: '', email: '', notes: '' });
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {loading ? (
            <div className="loading">Loading customers...</div>
          ) : (
            <div className="customers-list">
              {sortedCustomers.length === 0 ? (
                <div className="no-customers">No customers found</div>
              ) : (
                sortedCustomers.map(customer => (
                  <div
                    key={customer._id || customer.id}
                    className={`customer-card ${selectedCustomer?.customer._id === customer._id ? 'selected' : ''}`}
                  >
                    <div 
                      className="customer-card-content"
                      onClick={() => handleCustomerClick(customer)}
                    >
                      <div className="customer-name">{customer.name}</div>
                      <div className="customer-stats">
                        <span className="total-credits">
                          Total Credits: {customer.totalCredits.toFixed(2)}{settings.currency}
                        </span>
                        <span className="credit-count">
                          ({customer.creditCount} {customer.creditCount === 1 ? 'credit' : 'credits'})
                        </span>
                      </div>
                      {customer.lastCreditDate && (
                        <div className="last-credit-date">
                          Last: {format(new Date(customer.lastCreditDate), 'yyyy-MM-dd')}
                        </div>
                      )}
                    </div>
                    <button
                      className="btn-delete-customer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCustomer(customer);
                      }}
                      title="Delete customer"
                    >
                      🗑️
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {selectedCustomer && (
          <div className="customer-details-panel">
            <div className="customer-header">
              <h3>{selectedCustomer.customer.name}</h3>
              <button
                className="btn-close"
                onClick={() => {
                  setSelectedCustomer(null);
                  setCustomerCredits([]);
                  setCustomerPayments([]);
                  setShowPaymentForm(false);
                }}
              >
                ×
              </button>
            </div>

            <div className="customer-info">
              {selectedCustomer.customer.phone && (
                <div><strong>Phone:</strong> {selectedCustomer.customer.phone}</div>
              )}
              {selectedCustomer.customer.email && (
                <div><strong>Email:</strong> {selectedCustomer.customer.email}</div>
              )}
              {selectedCustomer.customer.notes && (
                <div><strong>Notes:</strong> {selectedCustomer.customer.notes}</div>
              )}
            </div>

            <div className="credits-summary">
              <div className="summary-card">
                <div className="summary-label">Total Credits</div>
                <div className="summary-value">
                  {selectedCustomer.totalCredits.toFixed(2)}{settings.currency}
                </div>
              </div>
              <div className="summary-card">
                <div className="summary-label">Total Payments</div>
                <div className="summary-value">
                  {selectedCustomer.totalPayments.toFixed(2)}{settings.currency}
                </div>
              </div>
              <div className={`summary-card ${selectedCustomer.balance >= 0 ? 'balance-positive' : 'balance-negative'}`}>
                <div className="summary-label">Remaining Balance</div>
                <div className="summary-value">
                  {selectedCustomer.balance.toFixed(2)}{settings.currency}
                </div>
              </div>
            </div>

            <div className="payment-section">
              <div className="payment-header">
                <h4>Record Payment</h4>
                <button
                  className="btn-payment-toggle"
                  onClick={() => setShowPaymentForm(!showPaymentForm)}
                >
                  {showPaymentForm ? 'Cancel' : '+ Record Payment'}
                </button>
              </div>

              {showPaymentForm && (
                <form onSubmit={handlePaymentSubmit} className="payment-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Amount:</label>
                      <div className="input-with-currency">
                        <input
                          type="number"
                          name="amount"
                          value={paymentForm.amount}
                          onChange={handlePaymentChange}
                          step="0.01"
                          placeholder="0.00"
                          required
                          min="0.01"
                        />
                        <span className="currency">{settings.currency}</span>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Date:</label>
                      <input
                        type="date"
                        name="date"
                        value={paymentForm.date}
                        onChange={handlePaymentChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Payment Method:</label>
                      <select
                        name="payment_method"
                        value={paymentForm.payment_method}
                        onChange={handlePaymentChange}
                        required
                      >
                        <option value="cash">Cash</option>
                        <option value="card">Card</option>
                        <option value="transfer">Transfer</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Notes:</label>
                    <textarea
                      name="notes"
                      value={paymentForm.notes}
                      onChange={handlePaymentChange}
                      rows="2"
                      placeholder="Payment notes..."
                    />
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary">
                      Record Payment
                    </button>
                  </div>
                </form>
              )}
            </div>

            <div className="credits-list">
              <h4>Credit History</h4>
              {customerCredits.length === 0 ? (
                <div className="no-credits">No credits found</div>
              ) : (
                <table className="credits-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Group</th>
                      <th>Time Group</th>
                      <th>Amount</th>
                      <th>Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerCredits
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map(credit => (
                        <tr key={credit._id || credit.id}>
                          <td>{format(new Date(credit.date), 'yyyy-MM-dd')}</td>
                          <td>{credit.group}</td>
                          <td>{credit.time_group}</td>
                          <td className="amount">
                            {credit.amount.toFixed(2)}{settings.currency}
                          </td>
                          <td>{credit.notes || '-'}</td>
                          <td>
                            <button
                              className="btn-delete-small"
                              onClick={() => handleDeleteCredit(credit._id || credit.id)}
                              title="Delete"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="payments-list">
              <h4>Payment History</h4>
              {customerPayments.length === 0 ? (
                <div className="no-payments">No payments found</div>
              ) : (
                <table className="payments-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Method</th>
                      <th>Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerPayments
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map(payment => (
                        <tr key={payment._id || payment.id}>
                          <td>{format(new Date(payment.date), 'yyyy-MM-dd')}</td>
                          <td className="amount payment-amount">
                            -{payment.amount.toFixed(2)}{settings.currency}
                          </td>
                          <td>{payment.payment_method || 'cash'}</td>
                          <td>{payment.notes || '-'}</td>
                          <td>
                            <button
                              className="btn-delete-small"
                              onClick={() => handleDeletePayment(payment._id || payment.id)}
                              title="Delete"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
      )}

      {activeSection === 'employees' && (
        <div className="employees-container">
          <div className="employees-content">
            <div className="employees-list-panel">
              <div className="list-header">
                <div className="search-bar">
                  <input
                    type="text"
                    placeholder="Search employees..."
                    value={employeeSearchQuery}
                    onChange={(e) => setEmployeeSearchQuery(e.target.value)}
                  />
                </div>
                <button 
                  onClick={() => setShowEmployeeForm(!showEmployeeForm)} 
                  className="btn btn-primary btn-add"
                >
                  {showEmployeeForm ? 'Cancel' : '+ Add Employee'}
                </button>
              </div>

              {showEmployeeForm && (
                <form onSubmit={handleAddNewEmployee} className="add-form">
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={newEmployeeForm.name}
                      onChange={handleEmployeeFormChange}
                      placeholder="Employee name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Monthly Salary *</label>
                    <div className="input-with-currency">
                      <input
                        type="number"
                        name="monthly_salary"
                        value={newEmployeeForm.monthly_salary}
                        onChange={handleEmployeeFormChange}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        required
                      />
                      <span className="currency">{settings.currency}</span>
                    </div>
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary">
                      Add Employee
                    </button>
                    <button 
                      type="button" 
                      onClick={() => {
                        setShowEmployeeForm(false);
                        setNewEmployeeForm({ name: '', monthly_salary: '' });
                      }}
                      className="btn btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              {employeesLoading ? (
                <div className="loading">Loading employees...</div>
              ) : (
                <div className="employees-list">
                  {filteredEmployees.length === 0 ? (
                    <div className="no-employees">No employees found</div>
                  ) : (
                    filteredEmployees.map(employee => (
                      <div
                        key={employee._id || employee.id}
                        className={`employee-card ${selectedEmployee?.employee._id === employee._id ? 'selected' : ''}`}
                      >
                        <div 
                          className="employee-card-content"
                          onClick={() => handleEmployeeClick(employee)}
                        >
                          <div className="employee-name">{employee.name}</div>
                          <div className="employee-stats">
                            <span className="salary">
                              Salary: {employee.monthly_salary.toFixed(2)}{settings.currency}/month
                            </span>
                          </div>
                        </div>
                        <button
                          className="btn-delete-employee"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteEmployee(employee);
                          }}
                          title="Delete employee"
                        >
                          🗑️
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {selectedEmployee && (
              <div className="employee-details-panel">
                <div className="employee-header">
                  <h3>{selectedEmployee.employee.name}</h3>
                  <button
                    className="btn-close"
                    onClick={() => {
                      setSelectedEmployee(null);
                      setEmployeeCredits([]);
                    }}
                  >
                    ×
                  </button>
                </div>

                <div className="employee-info">
                  <div><strong>Monthly Salary:</strong> {selectedEmployee.employee.monthly_salary.toFixed(2)}{settings.currency}</div>
                  {selectedEmployee.employee.phone && (
                    <div><strong>Phone:</strong> {selectedEmployee.employee.phone}</div>
                  )}
                  {selectedEmployee.employee.email && (
                    <div><strong>Email:</strong> {selectedEmployee.employee.email}</div>
                  )}
                  {selectedEmployee.employee.notes && (
                    <div><strong>Notes:</strong> {selectedEmployee.employee.notes}</div>
                  )}
                </div>

                <div className="credits-section">
                  <h4>Credit History</h4>
                  {employeeCredits.length === 0 ? (
                    <div className="no-credits">No credit records found</div>
                  ) : (
                    <table className="credits-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Group</th>
                          <th>Time Group</th>
                          <th>Amount</th>
                          <th>Notes</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {employeeCredits
                          .sort((a, b) => new Date(b.date) - new Date(a.date))
                          .map(credit => (
                            <tr key={credit._id || credit.id}>
                              <td>{format(new Date(credit.date), 'yyyy-MM-dd')}</td>
                              <td>{credit.group}</td>
                              <td>{credit.time_group}</td>
                              <td className="amount">
                                {credit.amount.toFixed(2)}{settings.currency}
                              </td>
                              <td>{credit.notes || '-'}</td>
                              <td>
                                <button
                                  className="btn-delete-small"
                                  onClick={() => handleDeleteEmployeeCredit(credit._id || credit.id)}
                                  title="Delete"
                                >
                                  🗑️
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomersTab;

